import Wallet, { SerializedWallet } from '../wallet'
import { Principal } from '@dfinity/principal'

import * as bip39 from '@scure/bip39'
import { wordlist as englishWordlist } from '@scure/bip39/wordlists/english'
import { Secp256k1KeyIdentity, Secp256k1PublicKey } from '@dfinity/identity-secp256k1'
import { Ed25519KeyIdentity } from '@dfinity/identity'
import { HDKey } from '@scure/bip32'
import Secp256k1 from 'secp256k1'
import { secp256k1ToPem } from '../../utils/identity/secp256kToPem'
import { IKeyChain, KeychainType } from './IKeychain'

export interface SerializedHDKeychain {
  type: KeychainType.HDKeychain
  mnemonic: string
  imported?: boolean
  accountsEnabled?: number
  wallets?: SerializedWallet[]
}

const privates = new WeakMap<
  IKeyChain,
  {
    wallets: Array<{ wallet: Wallet; index: number }>
    mnemonic?: string
    addAccount(index: number): Wallet
    accountsEnabled: number
  }
>()

class HDKeychain implements IKeyChain {
  type: KeychainType.HDKeychain = KeychainType.HDKeychain
  imported: boolean

  constructor() {
    this.imported = false

    privates.set(this, {
      wallets: [],
      mnemonic: undefined,
      accountsEnabled: 1,
      addAccount: (index) => {
        const _privates = privates.get(this)!

        const seed = bip39.mnemonicToSeedSync(_privates.mnemonic || '')
        const masterKey = HDKey.fromMasterSeed(seed)

        const { privateKey } = masterKey.derive(`m/44'/223'/0'/0/${index}`)
        const publicKey = Secp256k1.publicKeyCreate(privateKey!, false)
        const pub = Secp256k1PublicKey.fromRaw(publicKey.buffer as ArrayBuffer)

        const identity = Secp256k1KeyIdentity.fromKeyPair(pub.toRaw(), privateKey! as ArrayBuffer)

        const newWallet = new Wallet(index, identity)
        _privates.wallets.push({ wallet: newWallet, index })
        return newWallet
      },
    })
  }

  init(opts?: SerializedHDKeychain) {
    return this.deserialize(opts)
  }

  async serialize(): Promise<SerializedHDKeychain> {
    const _privates = privates.get(this)!
    if (!_privates.mnemonic) throw new Error('No mnemonic')

    return {
      imported: this.imported,
      mnemonic: _privates.mnemonic,
      accountsEnabled: _privates.accountsEnabled,
      type: this.type,
      wallets: _privates.wallets.map((w) => w.wallet.serialize()),
    }
  }

  async deserialize(opts?: SerializedHDKeychain) {
    const _privates = privates.get(this)!

    this.imported = !!opts?.imported
    if (opts?.accountsEnabled) _privates.accountsEnabled = opts.accountsEnabled
    if (opts?.mnemonic) {
      if (!bip39.validateMnemonic(opts.mnemonic, englishWordlist)) {
        throw new Error('Invalid mnemonic')
      }
      _privates.mnemonic = opts.mnemonic
    } else {
      _privates.mnemonic = bip39.generateMnemonic(englishWordlist)
    }

    if (opts?.wallets) {
      _privates.wallets = opts.wallets.map((seralized, index) => ({
        wallet: Wallet.fromSerialized(seralized),
        index,
      }))
    } else {
      for (let i = 0; i < _privates.accountsEnabled; i++) {
        _privates.addAccount(i)
      }
    }
  }

  async addNewAccount(): Promise<Wallet[]> {
    const _privates = privates.get(this)!

    _privates.addAccount(_privates.accountsEnabled)
    _privates.accountsEnabled += 1
    return _privates.wallets.map(({ wallet }) => wallet)
  }

  getPrincipals(): Promise<Principal[]> {
    const _privates = privates.get(this)!
    const principals = _privates.wallets.map(({ wallet }) => wallet.principal)
    return Promise.resolve(principals)
  }

  getIdentity(principal: string): Promise<Secp256k1KeyIdentity | Ed25519KeyIdentity> {
    const _privates = privates.get(this)!
    const wallet = _privates.wallets.find(
      (wallet) => wallet.wallet.principal.toString() === principal
    )
    if (!wallet) return Promise.reject('Unable to find wallet')

    return Promise.resolve(wallet.wallet.identity)
  }

  async getSessionIdentity(principal: string, host: string): Promise<Secp256k1KeyIdentity> {
    const _privates = privates.get(this)!
    const wallet = _privates.wallets.find(
      (wallet) => wallet.wallet.principal.toString() === principal
    )
    if (!wallet) return Promise.reject('Unable to find wallet')

    const seed = bip39.mnemonicToSeedSync(_privates.mnemonic || '')
    const masterKey = HDKey.fromMasterSeed(seed)

    const utf8Encode = new TextEncoder()

    const hostHash = await globalThis.crypto.subtle.digest('SHA-256', utf8Encode.encode(host))

    const uintArr = new Uint8Array(hostHash)
    const derivationPath = `m/44'/223'/0'/0/${Array.from(uintArr)
      .map((index) => `${index}`)
      .join('/')}`

    const { privateKey } = masterKey.derive(derivationPath)
    const publicKey = Secp256k1.publicKeyCreate(privateKey!, false)
    const pub = Secp256k1PublicKey.fromRaw(publicKey.buffer)

    return Promise.resolve(Secp256k1KeyIdentity.fromKeyPair(pub.rawKey, privateKey!))
  }

  async exportKeychain(): Promise<string> {
    const { mnemonic } = privates.get(this)!
    if (!mnemonic) throw new Error('No mnemonic')
    return mnemonic
  }

  async exportPem(principal: string): Promise<string> {
    const identity = await this.getIdentity(principal)

    return secp256k1ToPem(identity)
  }
}

export default HDKeychain
